import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { useAuth } from "../lib/auth";
import PackagesPromo from "../components/PackagesPromo";
import AuthHeader from "../components/AuthHeader";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Hinweis nach fehlgeschlagenem Login-Handoff aus der App (Browser-Checkout):
  // Der Einmal-Token war abgelaufen oder bereits benutzt. Nutzer kann sich hier
  // normal anmelden und den Checkout fortsetzen.
  const handoffParam = new URLSearchParams(location.search).get("handoff");
  const handoffNotice =
    handoffParam === "abgelaufen"
      ? "Der Anmelde-Link aus der App ist abgelaufen. Bitte melde dich an, um mit dem Abschluss im Browser fortzufahren."
      : handoffParam === "fehler"
        ? "Die automatische Anmeldung aus der App hat nicht geklappt. Bitte melde dich an, um fortzufahren."
        : null;

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      // Der Einrichtungs-Assistent wird NICHT mehr direkt hier angeboten: Er
      // speichert sofort über die API, was ohne aktives Abo serverseitig mit 402
      // (subscription_required) blockt. Stattdessen leitet die Abo-Sperre
      // (SubscriptionGate) zuerst zur Paketauswahl und bietet den Assistenten
      // erst nach aktivem Abo an.
      const from = (location.state as { from?: string } | null)?.from || "/";
      navigate(from, { replace: true });
    } catch (err) {
      const e2 = err as Error & { code?: string };
      setError(e2.message || "Anmeldung fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <AuthHeader mode="login" />

        <form onSubmit={handleSubmit} className="px-5 pt-4 pb-8 space-y-4 flex-1">
          {handoffNotice ? (
            <div
              className="text-sm text-[var(--color-app-text)] bg-[var(--color-brand-50,#eff6ff)] border border-[var(--color-brand-200,#bfdbfe)] rounded-xl px-3 py-2"
              data-testid="notice-handoff"
            >
              {handoffNotice}
            </div>
          ) : null}
          <div>
            <label className="label" htmlFor="email">E-Mail</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="input-email"
            />
          </div>
          <div>
            <label className="label" htmlFor="password">Passwort</label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="input-password"
            />
          </div>
          {error && (
            <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting}
            data-testid="button-login"
          >
            {submitting ? "Wird angemeldet …" : "Anmelden"}
          </button>

          <a
            href="/expo"
            className="flex items-center justify-center gap-2 mt-2 w-full rounded-xl border border-[var(--color-app-border)] px-4 py-3 text-[14px] font-semibold text-[var(--color-brand-500)] hover:bg-[var(--color-app-bg-elevated,#fff)] transition-colors"
            data-testid="link-expo-preview"
          >
            <Smartphone size={18} />
            App auf dem Handy öffnen (Expo Go)
          </a>

          <div className="pt-6 mt-6 border-t border-[var(--color-app-border)] text-center text-[12px] text-[var(--color-app-text-tertiary)]">
            Login mit Google / Apple folgt
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] text-[var(--color-app-text-tertiary)]">
            <a href="/agb" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-agb">AGB</a>
            <span aria-hidden="true">·</span>
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-datenschutz">Datenschutz</a>
            <span aria-hidden="true">·</span>
            <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-widerruf">Widerruf</a>
            <span aria-hidden="true">·</span>
            <a href="/impressum" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-impressum">Impressum</a>
          </div>
        </form>

        <PackagesPromo />
      </div>
    </div>
  );
}
